import type { ApplicationInsightsType as ApplicationInsights, ReactPluginType as ReactPlugin } from './applicationInsightsBundle';
import React from 'react';
import { UserContext } from "@webcore/models/coreModels.ts";
import { ConversionConfig, trackGoogleAnalyticsConversion } from "@webcore/services/telemetry/googleAnalytics.ts";
import { appConfig } from "@webcore/config/appConfig.ts";
import Log from "@webcore/services/log/log.ts";

/**
 * Core Telemetry Operations Interface
 * Basic telemetry operations without React-specific details
 */
export interface ITelemetryOperations {
  trackEvent(name: string, properties?: Record<string, any>, measurements?: Record<string, number>): void;
  trackPageView(name?: string, uri?: string, properties?: Record<string, any>): void;
  trackException(exception: Error, properties?: Record<string, any>): void;
  trackMetric(name: string, average: number, properties?: Record<string, any>): void;
  flushTelemetry(): void;
  setTelemetryAuthenticatedUserContext(userContext: UserContext | null): void;
  trackConversion(eventName: string, config?: ConversionConfig): Promise<void>;
}

/**
 * Full Telemetry Service Interface
 * Includes React integration for provider usage
 */
export interface ITelemetryService extends ITelemetryOperations {
  // React integration - returns the AppInsightsContext and ReactPlugin if available
  getReactPluginContext(): { AppInsightsContext: React.Context<ReactPlugin>; reactPlugin: ReactPlugin } | null;
}

/**
 * No-op implementation for when telemetry is not loaded or disabled
 */
export class NoOpTelemetryService implements ITelemetryService {
  trackEvent(_name: string, _properties?: Record<string, any>, _measurements?: Record<string, number>): void {}
  trackPageView(_name?: string, _uri?: string, _properties?: Record<string, any>): void {}
  trackException(_exception: Error, _properties?: Record<string, any>): void {}
  trackMetric(_name: string, _average: number, _properties?: Record<string, any>): void {}
  flushTelemetry(): void {}
  setTelemetryAuthenticatedUserContext(_userContext: UserContext | null): void {}
  getReactPluginContext(): { AppInsightsContext: React.Context<ReactPlugin>; reactPlugin: ReactPlugin } | null { return null; }
  async trackConversion(_eventName: string, _config?: ConversionConfig): Promise<void> {}
}

/**
 * Real implementation that wraps Application Insights and other telemetry services
 */
export class TelemetryService implements ITelemetryService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;
  private appInsightsContext: React.Context<ReactPlugin> | null = null;
  private isInitialized = false;

  constructor(
    appInsights: ApplicationInsights | null,
    reactPlugin: ReactPlugin | null,
    appInsightsContext: React.Context<ReactPlugin> | null,
    isInitialized: boolean
  ) {
    this.appInsights = appInsights;
    this.reactPlugin = reactPlugin;
    this.appInsightsContext = appInsightsContext;
    this.isInitialized = isInitialized;
  }

  private telemetryEnabled = (): boolean => {
    return this.appInsights !== null && this.isInitialized;
  };

  getReactPluginContext(): { AppInsightsContext: React.Context<ReactPlugin>; reactPlugin: ReactPlugin } | null {
    if (this.reactPlugin === null || this.appInsightsContext === null) {
      return null;
    }
    return {
      AppInsightsContext: this.appInsightsContext,
      reactPlugin: this.reactPlugin
    };
  }

  trackEvent(name: string, properties?: Record<string, any>, measurements?: Record<string, number>): void {
    if (!this.telemetryEnabled() || !this.appInsights) {
      return;
    }
    this.appInsights.trackEvent({ name, properties, measurements });
  }

  trackPageView(name?: string, uri?: string, properties?: Record<string, any>): void {
    if (!this.telemetryEnabled() || !this.appInsights) {
      return;
    }
    this.appInsights.trackPageView({ name, uri, properties });
  }

  trackException(exception: Error, properties?: Record<string, any>): void {
    if (!this.telemetryEnabled() || !this.appInsights) {
      return;
    }
    this.appInsights.trackException({ exception, properties });
  }

  trackMetric(name: string, average: number, properties?: Record<string, any>): void {
    if (!this.telemetryEnabled() || !this.appInsights) {
      return;
    }
    this.appInsights.trackMetric({ name, average, properties });
  }

  flushTelemetry(): void {
    if (!this.telemetryEnabled() || !this.appInsights) {
      return;
    }
    this.appInsights.flush();
  }

  setTelemetryAuthenticatedUserContext(userContext: UserContext | null): void {
    if (!this.telemetryEnabled() || !this.appInsights) {
      return;
    }

    if (userContext === null) {
      this.appInsights.setAuthenticatedUserContext('', '');
    } else {
      const userId = userContext.userId;
      const teamId = userContext.activeTeamId;
      this.appInsights.setAuthenticatedUserContext(userId, teamId);
    }
  }

  async trackConversion(eventName: string, config?: ConversionConfig): Promise<void> {
    const fullEventName = `conversion_event_${eventName}`;

    // Track in Application Insights
    this.trackEvent(fullEventName);

    // Track in Google Analytics as conversion
    await trackGoogleAnalyticsConversion(fullEventName, config);
  }
}

const telemetryConfigured = () => appConfig.applicationInsightsConnectionString.length > 0;

/**
 * Initialize Application Insights and return a TelemetryService
 * Returns NoOpTelemetryService immediately if telemetry is not configured,
 * avoiding any dynamic imports of Application Insights packages.
 */
export const telemetryServiceFactory = async (): Promise<ITelemetryService> => {
  // Check configuration BEFORE attempting any dynamic imports
  // This ensures we don't load Application Insights packages at all when not needed
  if (!telemetryConfigured()) {
    Log.debug("Application Insights telemetry not configured, so it won't be enabled (not production or missing connection string)");
    return new NoOpTelemetryService();
  }

  try {
    // Only reach here if telemetry is configured - now safe to dynamically load the packages
    // Import from bundle to ensure all Application Insights modules are in a single chunk
    const { ApplicationInsights, ReactPlugin, AppInsightsContext } = await import('./applicationInsightsBundle');

    // Create React plugin for automatic page view tracking
    const reactPlugin = new ReactPlugin();

    // Initialize Application Insights
    const appInsights = new ApplicationInsights({
      config: {
        connectionString: appConfig.applicationInsightsConnectionString,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            trackRouteChange: true,
            includePageViewAttributes: true,
          },
        },
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        maxAjaxCallsPerView: 20,
        disableAjaxTracking: false,
        disableFetchTracking: false,
        disableCorrelationHeaders: false,
        samplingPercentage: 100,
      },
    });

    // Load the Application Insights SDK
    appInsights.loadAppInsights();

    // Set global context properties
    appInsights.addTelemetryInitializer((envelope) => {
      envelope.tags = envelope.tags || {};
      envelope.tags['ai.application.ver'] = appConfig.buildHash;
      envelope.tags['ai.application.build'] = appConfig.buildDate;
      envelope.tags['ai.cloud.role'] = 'datafabriq-web-client';
      envelope.tags['ai.cloud.roleInstance'] = appConfig.environment;

      envelope.data = envelope.data || {};
      if (envelope.data.baseData) {
        envelope.data.baseData.properties = {
          ...envelope.data.baseData.properties,
          buildHash: appConfig.buildHash,
          environment: appConfig.environment,
        };
      }

      return true;
    });

    Log.debug('Application Insights initialized successfully');

    return new TelemetryService(appInsights, reactPlugin, AppInsightsContext, true);
  } catch (error) {
    Log.error('Failed to initialize Application Insights:', error);
    return new NoOpTelemetryService();
  }
};
